import React, { useEffect, useState, useCallback, useRef } from "react";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";

const TIERS = [
  { key: "mumbai",    label: "Mumbai",        color: "#7c3aed" },
  { key: "bangalore", label: "Bangalore",     color: "#db2777" },
  { key: "hyderabad", label: "Hyderabad",     color: "#ea580c" },
  { key: "chennai",   label: "Chennai",       color: "#0369a1" },
  { key: "pune",      label: "Pune",          color: "#d97706" },
  { key: "ahmedabad", label: "Ahmedabad",     color: "#0891b2" },
  { key: "kolkata",   label: "Kolkata",       color: "#16a34a" },
  { key: "tier1",     label: "Tier 1 Cities", color: "#4f46e5" },
  { key: "rest",      label: "Rest of India", color: "#059669" },
];

const EMPTY_TIER_CONFIG = () =>
  Object.fromEntries(TIERS.map((t) => [t.key, { value: "", type: "percentage" }]));

const compute = (base, value, type) => {
  const val = parseFloat(value);
  if (!base || isNaN(val) || value === "") return base;
  if (type === "percentage") return Math.round(base + (base * val) / 100);
  return Math.round(base + val);
};

export default function GeoPrice() {
  const [pujas, setPujas] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceType, setPriceType] = useState("puja"); // "puja" | "pandit"

  const [tierConfig, setTierConfig] = useState(EMPTY_TIER_CONFIG);
  const [manualPrices, setManualPrices] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const cancelEditRef = useRef(false);
  const originalEditValueRef = useRef(null);
  const autoSaveReady = useRef(false);
  const saveTimerRef = useRef(null);

  const fetchPujas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.get("service/list");
      setPujas(response?.data || []);
    } catch {
      toast.error("Failed to fetch puja list");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGeoPrices = useCallback(async (type) => {
    try {
      const response = await ApiService.get("service/geo-prices");
      const allData = response?.data || {};
      const grouped = allData[type] || {};

      const newManualPrices = {};
      const detectedTierConfig = {};

      for (const [serviceId, cities] of Object.entries(grouped)) {
        for (const [city, data] of Object.entries(cities)) {
          if (data.type === "manual") {
            newManualPrices[`${serviceId}_${city}`] = data.finalPrice;
          } else if (!detectedTierConfig[city]) {
            detectedTierConfig[city] = { type: data.type, value: data.value ?? "" };
          }
        }
      }

      setManualPrices(Object.keys(newManualPrices).length > 0 ? newManualPrices : {});
      setTierConfig(() => {
        const next = EMPTY_TIER_CONFIG();
        for (const [city, cfg] of Object.entries(detectedTierConfig)) {
          next[city] = cfg;
        }
        return next;
      });
    } catch {
      // non-critical
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPujas();
    fetchGeoPrices("puja");
  }, [fetchPujas, fetchGeoPrices]);

  // When priceType switches: disable auto-save, reset state, re-fetch for new type
  useEffect(() => {
    autoSaveReady.current = false;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setTierConfig(EMPTY_TIER_CONFIG());
    setManualPrices({});
    setEditingCell(null);
    fetchGeoPrices(priceType).then(() => {
      setTimeout(() => { autoSaveReady.current = true; }, 1000);
    });
  }, [priceType]);

  // Enable auto-save after initial pujas load
  useEffect(() => {
    if (!autoSaveReady.current && pujas.length > 0) {
      const t = setTimeout(() => { autoSaveReady.current = true; }, 1000);
      return () => clearTimeout(t);
    }
  }, [pujas]);

  // Debounced auto-save
  useEffect(() => {
    if (!autoSaveReady.current || pujas.length === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const payload = pujas.map((puja) => {
          const base = parseFloat(priceType === "pandit" ? puja.acharyaBilling : puja.price) || 0;
          const tierPrices = Object.fromEntries(
            TIERS.map((t) => {
              const cellKey = `${puja.id}_${t.key}`;
              const hasManual = manualPrices[cellKey] !== undefined;
              return [
                t.key,
                {
                  type:  hasManual ? "manual" : tierConfig[t.key].type,
                  value: hasManual ? null : (tierConfig[t.key].value !== "" ? parseFloat(tierConfig[t.key].value) : null),
                  price: hasManual ? manualPrices[cellKey] : compute(base, tierConfig[t.key].value, tierConfig[t.key].type),
                },
              ];
            })
          );
          return { serviceId: puja.id, priceType, ...tierPrices };
        });
        await ApiService.post("service/geo-prices/bulk", { prices: payload });
        toast.success("Geo prices saved");
      } catch (err) {
        toast.error(err?.response?.data?.error || "Failed to save geo prices");
      } finally {
        setSaving(false);
      }
    }, 1000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [tierConfig, manualPrices]);

  const updateConfig = (tierKey, field, value) => {
    setTierConfig((prev) => ({
      ...prev,
      [tierKey]: { ...prev[tierKey], [field]: value },
    }));
  };

  const commitCellEdit = (cellKey) => {
    if (cancelEditRef.current) {
      cancelEditRef.current = false;
      setEditingCell(null);
      setEditingValue("");
      originalEditValueRef.current = null;
      return;
    }
    const num = parseFloat(editingValue);
    const original = parseFloat(originalEditValueRef.current);
    if (!isNaN(num) && String(editingValue).trim() !== "" && Math.round(num) !== Math.round(original)) {
      const roundedNum = Math.round(num);
      setManualPrices((prev) => ({ ...prev, [cellKey]: roundedNum }));

      // Derive the implied % from the manual price and push it into the column's
      // tier config so all other pujas in this city reflect the same increase.
      const lastUnderscore = cellKey.lastIndexOf('_');
      const pujaId = cellKey.slice(0, lastUnderscore);
      const city   = cellKey.slice(lastUnderscore + 1);
      const puja   = pujas.find(p => String(p.id) === pujaId);
      if (puja) {
        const base = parseFloat(isPandit ? puja.acharyaBilling : puja.price) || 0;
        if (base > 0) {
          const impliedPct = Math.round(((roundedNum - base) / base) * 100);
          setTierConfig(prev => ({
            ...prev,
            [city]: { type: 'percentage', value: impliedPct },
          }));
        }
      }
    }
    setEditingCell(null);
    setEditingValue("");
    originalEditValueRef.current = null;
  };

  const clearManualPrice = (cellKey) => {
    setManualPrices((prev) => { const n = { ...prev }; delete n[cellKey]; return n; });
  };

  const isPandit = priceType === "pandit";

  return (
    <div className="row">
      {loading && <Loader />}
      <div className="col-12">
        <div className="card shadow-sm" style={{ borderRadius: 12, border: "1px solid #e5e7eb" }}>

          {/* Card Header */}
          <div
            className="card-header d-flex align-items-center justify-content-between"
            style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb", borderRadius: "12px 12px 0 0", padding: "16px 24px" }}
          >
            <div>
              <h5 className="mb-0 fw-semibold" style={{ color: "#111827" }}>Geo Pricing</h5>
              <small style={{ color: "#6b7280" }}>
                {isPandit
                  ? "Adjust pandit billing amounts per city — changes save automatically"
                  : "Adjust puja prices per city — changes save automatically"}
              </small>
            </div>
            <div className="d-flex align-items-center gap-3">

              {/* Toggle slider */}
              <div
                style={{
                  display: "flex",
                  background: "#e5e7eb",
                  borderRadius: 8,
                  padding: 3,
                  gap: 2,
                }}
              >
                {[
                  { value: "puja",   label: "Puja Pricing" },
                  { value: "pandit", label: "Pandit Billing" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setPriceType(opt.value)}
                    style={{
                      padding: "5px 16px",
                      borderRadius: 6,
                      border: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      background: priceType === opt.value ? "#ffffff" : "transparent",
                      color: priceType === opt.value ? "#111827" : "#6b7280",
                      boxShadow: priceType === opt.value ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div style={{ position: "relative" }}>
                <i
                  className="fa fa-search"
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13 }}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search puja..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 30, width: 200, borderRadius: 8 }}
                />
              </div>
              {saving && (
                <span style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  Saving…
                </span>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>

                {/* Header */}
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ padding: "14px 20px", borderBottom: "2px solid #e5e7eb", color: "#374151", fontWeight: 600, fontSize: 13 }}>
                      Puja Name
                    </th>
                    <th style={{ padding: "14px 16px", borderBottom: "2px solid #e5e7eb", color: "#374151", fontWeight: 600, fontSize: 13, minWidth: 120, textAlign: "center" }}>
                      {isPandit ? "Pandit Billing" : "Base Price"}
                    </th>
                    {TIERS.map((t) => (
                      <th key={t.key} style={{ padding: "10px 16px", borderBottom: "2px solid #e5e7eb", minWidth: 150 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.color + "18", border: `1px solid ${t.color}40`, borderRadius: 6, padding: "2px 10px 2px 6px", marginBottom: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.color, display: "inline-block" }} />
                          <span style={{ color: t.color, fontWeight: 600, fontSize: 12 }}>{t.label}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <input
                            type="number"
                            style={{ width: 58, fontSize: 12, padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 6, outline: "none", background: "#fff", color: "#374151" }}
                            placeholder={tierConfig[t.key].type === "percentage" ? "10" : "500"}
                            step="1"
                            min={tierConfig[t.key].type === "percentage" ? -100 : -50000}
                            max={tierConfig[t.key].type === "percentage" ? 100 : 50000}
                            value={tierConfig[t.key].value}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const isPerc = tierConfig[t.key].type === "percentage";
                              const lo = isPerc ? -100 : -50000;
                              const hi = isPerc ? 100 : 50000;
                              if (raw === "" || raw === "-") { updateConfig(t.key, "value", raw); return; }
                              const num = parseInt(raw, 10);
                              if (!isNaN(num) && num >= lo && num <= hi) updateConfig(t.key, "value", num);
                            }}
                          />
                          <select
                            className="no-picker"
                            style={{ width: 56, fontSize: 12, padding: "2px 4px", border: "1px solid #d1d5db", borderRadius: 6, outline: "none", background: "#fff", color: "#374151", cursor: "pointer" }}
                            value={tierConfig[t.key].type}
                            onChange={(e) => setTierConfig((prev) => ({ ...prev, [t.key]: { value: "", type: e.target.value } }))}
                          >
                            <option value="percentage">%</option>
                            <option value="flat">Fixed</option>
                          </select>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody>
                  {(() => {
                    const filtered = pujas.filter((p) => p.title?.toLowerCase().includes(search.toLowerCase()));
                    if (filtered.length === 0 && !loading) return (
                      <tr>
                        <td colSpan={2 + TIERS.length} style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                          {search ? `No pujas matching "${search}"` : "No pujas found"}
                        </td>
                      </tr>
                    );
                    return filtered.map((puja, idx) => {
                      const base = parseFloat(isPandit ? puja.acharyaBilling : puja.price) || 0;
                      const isEven = idx % 2 === 0;

                      return (
                        <tr
                          key={puja.id}
                          style={{ background: isEven ? "#ffffff" : "#f9fafb", transition: "background 0.15s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = isEven ? "#ffffff" : "#f9fafb")}
                        >
                          <td style={{ padding: "12px 20px", borderBottom: "1px solid #f3f4f6" }}>
                            <span style={{ fontWeight: 500, color: "#111827", fontSize: 14 }}>{puja.title}</span>
                          </td>

                          <td style={{ padding: "12px 16px", borderBottom: "1px solid #f3f4f6", textAlign: "center" }}>
                            {base > 0 ? (
                              <span style={{ background: "#f3f4f6", color: "#374151", borderRadius: 6, padding: "3px 10px", fontWeight: 600, fontSize: 13 }}>
                                ₹{base.toLocaleString("en-IN")}
                              </span>
                            ) : (
                              <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                            )}
                          </td>

                          {TIERS.map((t) => {
                            const cellKey = `${puja.id}_${t.key}`;
                            const hasManual = manualPrices[cellKey] !== undefined;
                            const computedPrice = compute(base, tierConfig[t.key].value, tierConfig[t.key].type);
                            const price = hasManual ? manualPrices[cellKey] : computedPrice;
                            const isAdjusted = price !== base;
                            const diff = price - base;
                            const isEditing = editingCell === cellKey;

                            return (
                              <td key={t.key} style={{ padding: "12px 16px", borderBottom: "1px solid #f3f4f6", textAlign: "center" }}>
                                {base === 0 ? (
                                  <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                                ) : isEditing ? (
                                  <input
                                    type="number"
                                    autoFocus
                                    className="form-control form-control-sm text-center"
                                    style={{ width: 90, margin: "0 auto", fontWeight: 600, fontSize: 13 }}
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onBlur={() => commitCellEdit(cellKey)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") e.target.blur();
                                      if (e.key === "Escape") { cancelEditRef.current = true; e.target.blur(); }
                                    }}
                                  />
                                ) : (
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <span
                                      onClick={() => { setEditingCell(cellKey); setEditingValue(String(price)); originalEditValueRef.current = String(price); }}
                                      title="Click to set price manually"
                                      style={{
                                        background: !isAdjusted ? "#f3f4f6" : diff < 0 ? "#fef2f2" : t.color + "15",
                                        color: !isAdjusted ? "#6b7280" : diff < 0 ? "#dc2626" : t.color,
                                        border: hasManual ? `2px dashed ${t.color}` : !isAdjusted ? "1px solid #e5e7eb" : diff < 0 ? "1px solid #fca5a5" : `1px solid ${t.color}40`,
                                        borderRadius: 6,
                                        padding: "3px 10px",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        display: "inline-block",
                                        cursor: "pointer",
                                      }}
                                    >
                                      ₹{price.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                )}
                                {base > 0 && isAdjusted && !isEditing && (
                                  <div style={{ fontSize: 11, color: diff < 0 ? "#dc2626" : t.color, marginTop: 3, opacity: 0.85 }}>
                                    {diff < 0 ? "−" : "+"}₹{Math.abs(diff).toLocaleString("en-IN")}
                                    {hasManual && (
                                      <span onClick={() => clearManualPrice(cellKey)} style={{ marginLeft: 4, color: "#dc2626", cursor: "pointer", fontWeight: 600 }}>
                                        (clear)
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "10px 24px", borderTop: "1px solid #e5e7eb", background: "#f9fafb", borderRadius: "0 0 12px 12px", fontSize: 12, color: "#9ca3af" }}>
            {search
              ? `${pujas.filter((p) => p.title?.toLowerCase().includes(search.toLowerCase())).length} of ${pujas.length} pujas`
              : `${pujas.length} puja${pujas.length !== 1 ? "s" : ""}`}
          </div>
        </div>
      </div>
    </div>
  );
}
